import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';
import { isFreeCourse } from '../../services/courseAccess';

export default function MarcheCours() {
  const [coursParCategorie, setCoursParCategorie] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAchatModal, setShowAchatModal] = useState(false);
  const [selectedCours, setSelectedCours] = useState(null);
  const [codePin, setCodePin] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [activeCategorie, setActiveCategorie] = useState('tous');
  const navigate = useNavigate();

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchCours = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/enseignant/cours/get_cours_by_categorie.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setCoursParCategorie(data.cours_par_categorie);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAcheter = (cours) => {
    if (isFreeCourse(cours)) { handleLireCours(cours.id_cours); return; }
    if (cours.deja_achete === true || cours.deja_achete === 1) {
      toast('Vous avez déjà acheté ce cours');
      return;
    }
    setSelectedCours(cours);
    setShowAchatModal(true);
    setCodePin('');
  };

  const handleLireCours = (idCours) => {
    navigate(`/etudiant/cours/${idCours}/lire`);
  };

  const confirmerAchat = async () => {
    if (!codePin || codePin.length < 4) {
      toast.error('Code PIN invalide (minimum 4 chiffres)');
      return;
    }
    
    setPurchasing(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('id_cours', selectedCours.id_cours);
      formData.append('code_pin', codePin);
      
      const response = await fetch(`${API_BASE}/etudiant/cours/acheter_cours.php`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Cours acheté avec succès !`);
        setShowAchatModal(false);
        setSelectedCours(null);
        setCodePin('');
        fetchCours();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'achat');
    } finally {
      setPurchasing(false);
    }
  };

  useEffect(() => {
    fetchCours();
  }, []);

  const categories = [
    { id: 'tous', label: 'Tous' },
    { id: 'primaire', label: 'Primaire', color: 'orange' },
    { id: 'college_lycee', label: 'Collège & Lycée', color: 'blue' },
    { id: 'universite', label: 'Université', color: 'purple' },
    { id: 'libre', label: 'Libre', color: 'emerald' }
  ];

  const getAllCours = () => {
    const tous = [];
    Object.values(coursParCategorie).forEach(liste => {
      tous.push(...liste);
    });
    return tous;
  };

  const getCoursToDisplay = () => {
    if (activeCategorie === 'tous') return getAllCours();
    return coursParCategorie[activeCategorie] || [];
  };

  // Composant de rendu du média avec contrôles libres
  const renderMedia = (cours) => {
    if (!cours.media_url) return null;

    const mediaUrl = config.normalizePostMedia(cours.media_url);
    const mediaType = cours.media_type;

    switch (mediaType) {
      case 'image':
        return (
          <div className="relative h-48 overflow-hidden bg-gray-100">
            <img 
              src={mediaUrl} 
              alt={cours.titre}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div class="flex items-center justify-center h-full text-gray-400">
                    <span class="text-4xl">🖼️</span>
                  </div>
                `;
              }}
            />
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
              📷 Image
            </div>
          </div>
        );
      
      case 'pdf':
        return (
          <div className="relative h-32 bg-red-50 flex items-center justify-center gap-3 p-4 border-b">
            <span className="text-5xl">📄</span>
            <div className="text-left">
              <p className="font-medium text-gray-800 text-sm">Document PDF</p>
              <p className="text-xs text-gray-500">Cliquez pour visualiser</p>
            </div>
            <div className="absolute top-2 right-2 bg-red-500/80 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
              PDF
            </div>
          </div>
        );
      
      case 'audio':
        return (
          <div className="relative bg-gradient-to-r from-green-50 to-emerald-50 p-3 border-b">
            <div className="flex items-center gap-3">
              <span className="text-3xl flex-shrink-0">🎵</span>
              <div className="flex-1 min-w-0">
                <audio 
                  controls 
                  className="w-full h-10"
                  controlsList="nodownload" // Empêche le téléchargement mais garde les contrôles
                  preload="metadata"
                >
                  <source src={mediaUrl} />
                  Votre navigateur ne supporte pas l'audio.
                </audio>
              </div>
            </div>
            <div className="absolute top-2 right-2 bg-green-500/80 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
              🎵 Audio
            </div>
          </div>
        );
      
      case 'video':
        return (
          <div className="relative bg-black overflow-hidden">
            <video 
              controls
              className="w-full max-h-72"
              controlsList="nodownload" // Empêche le téléchargement mais garde les contrôles
              preload="metadata"
              poster={null}
            >
              <source src={mediaUrl} />
              Votre navigateur ne supporte pas la vidéo.
            </video>
            <div className="absolute top-2 right-2 bg-purple-500/80 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
              🎬 Vidéo
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const coursToDisplay = getCoursToDisplay();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800">📚 Marché des cours</h1>
              <p className="text-sm text-gray-500 hidden sm:block">Découvrez et achetez des cours de qualité</p>
            </div>
            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {coursToDisplay.length} cours
            </div>
          </div>
        </div>
      </div>

      {/* Filtres - Version mobile optimisée */}
      <div className="max-w-7xl mx-auto px-4 py-4 overflow-x-auto">
        <div className="flex gap-2 sm:gap-3 pb-2 min-w-max">
          {categories.map((cat) => {
            const count = cat.id === 'tous' 
              ? getAllCours().length 
              : (coursParCategorie[cat.id]?.length || 0);
            
            const isActive = activeCategorie === cat.id;
            const colorClasses = {
              orange: isActive ? 'bg-orange-600 text-white' : 'hover:bg-orange-50 text-orange-700',
              blue: isActive ? 'bg-blue-600 text-white' : 'hover:bg-blue-50 text-blue-700',
              purple: isActive ? 'bg-purple-600 text-white' : 'hover:bg-purple-50 text-purple-700',
              emerald: isActive ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-50 text-emerald-700',
              tous: isActive ? 'bg-gray-800 text-white' : 'hover:bg-gray-100 text-gray-700'
            };

            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategorie(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  isActive 
                    ? `${colorClasses[cat.color || 'tous']} shadow-md shadow-gray-200 scale-105`
                    : `bg-white border border-gray-200 ${colorClasses[cat.color || 'tous']}`
                }`}
              >
                <span className="flex items-center gap-2">
                  {cat.label}
                  <span className={`text-xs ${isActive ? 'bg-white/20' : 'bg-gray-200'} px-1.5 py-0.5 rounded-full`}>
                    {count}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Grille des cours */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {coursToDisplay.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📚</div>
            <p className="text-gray-500 text-lg">Aucun cours disponible</p>
            <p className="text-sm text-gray-400">Revenez plus tard pour découvrir de nouveaux cours</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {coursToDisplay.map((cours) => {
              const estAchete = cours.deja_achete === true || Number(cours.deja_achete) > 0;
              const libre = isFreeCourse(cours);
              const categorieInfo = categories.find(c => c.id === cours.categorie);
              
              return (
                <div 
                  key={cours.id_cours} 
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col"
                >
                  {/* Media avec contrôles */}
                  {renderMedia(cours)}
                  
                  {/* Contenu */}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="font-semibold text-gray-800 line-clamp-2 flex-1">
                        {cours.titre}
                      </h3>
                      <span className="text-lg font-bold text-green-600 whitespace-nowrap">
                        {libre ? 'Accès libre' : Number(cours.prix).toLocaleString('fr-FR') + ' F'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        cours.categorie === 'primaire' ? 'bg-orange-100 text-orange-700' :
                        cours.categorie === 'college_lycee' ? 'bg-blue-100 text-blue-700' :
                        cours.categorie === 'universite' ? 'bg-purple-100 text-purple-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {categorieInfo?.label || cours.categorie}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{cours.nb_acheteurs || 0} acheteurs</span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2 flex-1">
                      {cours.description || 'Aucune description'}
                    </p>
                    
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      {(estAchete || libre) ? (
                        <button 
                          onClick={() => handleLireCours(cours.id_cours)}
                          className="w-full bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 transition text-sm font-medium flex items-center justify-center gap-2"
                        >
                          {libre ? 'Lire gratuitement' : 'Lire le cours'}
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleAcheter(cours)}
                          className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <span>🛒</span> Acheter
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal d'achat */}
      {showAchatModal && selectedCours && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setShowAchatModal(false)}
        >
          <div 
            className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 bg-gradient-to-r from-blue-600 to-green-600">
              <h3 className="text-white text-lg font-bold text-center">Confirmer l'achat</h3>
            </div>
            
            {/* Body */}
            <div className="p-5">
              <div className="text-center mb-6">
                <p className="text-gray-600 text-sm">Vous allez acheter</p>
                <p className="font-bold text-gray-800 text-lg mt-1">{selectedCours.titre}</p>
                <p className="text-2xl font-bold text-green-600 mt-2">
                  {selectedCours.prix.toLocaleString()} FCFA
                </p>
                <div className="mt-2 inline-block bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-500">
                  👤 {selectedCours.nb_acheteurs || 0} acheteurs
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code PIN de votre carte
                </label>
                <input 
                  type="password" 
                  value={codePin} 
                  onChange={(e) => setCodePin(e.target.value)} 
                  placeholder="••••••" 
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-center text-xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  autoFocus 
                  maxLength={6}
                />
                <p className="text-xs text-gray-400 mt-1 text-center">Entrez votre code PIN à 4 ou 6 chiffres</p>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={confirmerAchat} 
                  disabled={purchasing} 
                  className="flex-1 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50 transition font-medium"
                >
                  {purchasing ? 'Achat...' : 'Acheter'}
                </button>
                <button 
                  onClick={() => setShowAchatModal(false)} 
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS supplémentaires */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        /* Styles pour les contrôles audio/vidéo */
        audio::-webkit-media-controls-panel,
        video::-webkit-media-controls-panel {
          background-color: rgba(0,0,0,0.05);
        }
        
        audio:hover::-webkit-media-controls-panel,
        video:hover::-webkit-media-controls-panel {
          background-color: rgba(0,0,0,0.1);
        }
        
        /* Pour Firefox */
        audio::-moz-range-track,
        video::-moz-range-track {
          background-color: #e5e7eb;
        }
      `}</style>
    </div>
  );
}